import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { FiHeart, FiShare2, FiShoppingCart, FiCheck } from 'react-icons/fi';
import { gameAPI } from '../../services/api';
import { useTelegram } from '../../hooks/useTelegram';
import { useCart } from '../../context/CartContext';
import { useFavorites } from '../../context/FavoritesContext';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Page = styled.div`
  min-height: 100vh;
  background: #000;
  padding-bottom: 100px;
  animation: ${fadeIn} 0.3s ease;
`;

const Hero = styled.div`
  height: 400px;
  position: relative;
  overflow: hidden;
`;

const HeroImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const HeroGradient = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 220px;
  background: linear-gradient(transparent, #000);
`;

const TopBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  z-index: 5;
`;

const IconBtn = styled.button`
  background: rgba(0,0,0,0.45);
  backdrop-filter: blur(12px);
  border: none;
  color: ${p => p.$active ? '#ff3b30' : 'rgba(255,255,255,0.85)'};
  width: 42px; height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  &:active { transform: scale(0.9); }
`;

const Body = styled.div`
  padding: 0 16px;
  margin-top: -50px;
  position: relative;
  z-index: 3;
`;

const GameTitle = styled.h1`
  font-size: 26px;
  font-weight: 800;
  color: #fff;
  margin: 0 0 14px;
  line-height: 1.2;
  letter-spacing: -0.5px;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
`;

const MetaChip = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  background: rgba(255,255,255,0.06);
  padding: 6px 12px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 500;
  color: rgba(255,255,255,0.55);
`;

const PlatChip = styled(MetaChip)`
  background: rgba(0,111,255,0.12);
  color: #4da3ff;
  font-weight: 600;
  border: 1px solid rgba(0,111,255,0.2);
`;

const GenreChip = styled(MetaChip)`
  background: rgba(255,214,10,0.08);
  color: #ffd60a;
`;

/* Price section */
const PriceSection = styled.div`
  background: rgba(0,111,255,0.03);
  border: 1px solid rgba(0,111,255,0.3);
  border-radius: 20px;
  padding: 18px;
  margin-bottom: 24px;
  box-shadow: 0 0 15px rgba(0,111,255,0.15);
`;

const PriceSectionTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: rgba(255,255,255,0.5);
  margin-bottom: 16px;
`;

const RegionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  margin: 0 -16px;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${p => p.$selected ? 'rgba(0,111,255,0.12)' : 'transparent'};
  border: 1px solid ${p => p.$selected ? 'rgba(0,111,255,0.35)' : 'transparent'};
  &:active { transform: scale(0.98); opacity: 0.9; }
  & + & { border-top: ${p => p.$selected ? 'none' : '1px solid rgba(255,255,255,0.04)'}; }
`;

const RegionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RegionFlag = styled.img`
  width: 56px;
  height: 44px;
  object-fit: cover;
  border-radius: 8px;
  flex-shrink: 0;
`;

const RegionName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: rgba(255,255,255,0.8);
  display: flex;
  align-items: center;
  gap: 6px;
`;

const BestBadge = styled.img`
  width: 28px;
  height: 28px;
  object-fit: contain;
  margin-left: 6px;
  flex-shrink: 0;
`;

const LangBadges = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 3px;
`;

const LangBadge = styled.span`
  font-size: 9px;
  font-weight: 500;
  color: rgba(255,255,255,0.75);
  background: rgba(0,111,255,0.1);
  border: 1px solid rgba(0,111,255,0.15);
  padding: 2px 5px;
  border-radius: 4px;
`;

const RegionPriceWrap = styled.div`
  text-align: right;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RegionPrice = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: ${p => p.$best ? '#00c853' : '#fff'};
  letter-spacing: -0.3px;
`;

const RegionOld = styled.div`
  font-size: 12px;
  color: rgba(255,255,255,0.2);
  text-decoration: line-through;
`;

const DiscBadge = styled.span`
  background: #ff3b30;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 8px;
`;

const DiscountNote = styled.div`
  font-size: 12px;
  color: #ff9500;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(0,111,255,0.15);
`;

/* Specs */
const SpecsSectionTitle = styled.h3`
  font-size: 20px;
  font-weight: 800;
  color: #fff;
  margin: 28px 0 16px;
  letter-spacing: -0.3px;
`;

const SpecsCard = styled.div`
  background: rgba(0,111,255,0.03);
  border: 1px solid rgba(0,111,255,0.3);
  border-radius: 20px;
  padding: 4px 18px;
  box-shadow: 0 0 15px rgba(0,111,255,0.1);
`;

const SpecRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 0;
  border-bottom: 1px solid rgba(0,111,255,0.1);
  font-size: 15px;
  &:last-child { border-bottom: none; }
`;

const SpecLabel = styled.span`
  color: rgba(255,255,255,0.35);
  font-weight: 400;
`;

const SpecVal = styled.span`
  color: #fff;
  font-weight: 600;
`;

const CheckIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  font-size: 12px;
  margin-right: 6px;
  background: ${p => p.$yes ? 'rgba(0,200,83,0.15)' : 'rgba(255,59,48,0.1)'};
  color: ${p => p.$yes ? '#00c853' : '#ff3b30'};
`;

/* Bottom bar */
const BottomBar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0,0,0,0.95);
  backdrop-filter: blur(20px);
  border-top: 1px solid rgba(0,111,255,0.3);
  padding: 14px 16px;
  padding-bottom: calc(14px + env(safe-area-inset-bottom, 0px));
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 100;
`;

const BottomLeft = styled.div``;

const BottomPrice = styled.div`
  font-size: 22px;
  font-weight: 800;
  color: #fff;
  letter-spacing: -0.5px;
`;

const BottomSaving = styled.div`
  font-size: 12px;
  color: #00c853;
  font-weight: 600;
  margin-top: 2px;
`;

const BottomHint = styled.div`
  font-size: 11px;
  color: rgba(255,255,255,0.3);
  margin-top: 2px;
`;

const OrderBtn = styled.button`
  background: ${p => p.$disabled ? 'rgba(255,255,255,0.08)' : p.$inCart ? 'rgba(0,200,83,0.12)' : 'linear-gradient(135deg, #006FFF, #0055cc)'};
  color: ${p => p.$disabled ? 'rgba(255,255,255,0.35)' : p.$inCart ? '#00c853' : '#fff'};
  border: ${p => p.$inCart ? '1px solid rgba(0,200,83,0.25)' : 'none'};
  padding: 14px 28px;
  border-radius: 14px;
  font-size: 15px;
  font-weight: 700;
  cursor: ${p => p.$disabled ? 'default' : 'pointer'};
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  transition: all 0.2s;
  box-shadow: ${p => !p.$disabled && !p.$inCart ? '0 0 20px rgba(0,111,255,0.3)' : 'none'};
  &:active { transform: ${p => p.$disabled ? 'none' : 'scale(0.97)'}; opacity: ${p => p.$disabled ? 1 : 0.9}; }
`;

const Loading = styled.div`
  min-height: 100vh;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255,255,255,0.3);
  font-size: 15px;
`;

const REGIONS = [
  { key: 'tr', flag: '/icons/flag-tr.png?v=8', name: 'Турция', currency: 'TL' },
  { key: 'ua', flag: '/icons/flag-ua.png?v=8', name: 'Украина', currency: 'UAH' },
  { key: 'in', flag: '/icons/flag-in.png?v=8', name: 'Индия', currency: 'INR' },
];

const GameDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tg, hapticFeedback } = useTelegram();
  const { addItem, removeItem, isInCart } = useCart();
  const { toggleFavorite, isFavorite } = useFavorites();
  const [game, setGame] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);

  useEffect(() => {
    if (tg) {
      tg.setHeaderColor('#000000');
      const goBack = () => navigate(-1);
      tg.BackButton.show();
      tg.BackButton.onClick(goBack);
      return () => {
        tg.BackButton.offClick(goBack);
        tg.BackButton.hide();
      };
    }
  }, [tg, navigate]);

  useEffect(() => {
    gameAPI.getGame(id).then(setGame).catch(() => {});
  }, [id]);

  if (!game) return <Loading>Загрузка...</Loading>;

  const gameId = game.id || game.sony_id;
  const fav = isFavorite(gameId);
  const inCart = isInCart(gameId);
  const platforms = game.platform?.includes('/') ? game.platform.split('/') : [game.platform];

  // Find min price & max old price for savings
  let minPrice = Infinity, minRegion = 'tr', maxOldPrice = 0;
  REGIONS.forEach(r => {
    const p = game.prices?.[r.key]?.price_rub;
    const old = game.prices?.[r.key]?.old_price_rub;
    if (p && p < minPrice) { minPrice = p; minRegion = r.key; }
    if (old && old > maxOldPrice) maxOldPrice = old;
  });
  if (minPrice === Infinity) minPrice = 0;

  const selectedPrice = selectedRegion ? game.prices?.[selectedRegion]?.price_rub : null;
  const selectedOld = selectedRegion ? game.prices?.[selectedRegion]?.old_price_rub : null;
  const saving = selectedOld && selectedPrice ? selectedOld - selectedPrice : (maxOldPrice > minPrice ? maxOldPrice - minPrice : 0);

  return (
    <Page>
      <Hero>
        {game.image_url ? (
          <HeroImg src={game.image_url} alt={game.title} referrerPolicy="no-referrer" />
        ) : (
          <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #111, #000)' }} />
        )}
        <HeroGradient />
        <TopBar>
          <IconBtn onClick={() => { hapticFeedback?.('light'); navigate(-1); }} style={{marginRight:'auto'}}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </IconBtn>
          <IconBtn $active={fav} onClick={() => { hapticFeedback?.('light'); toggleFavorite(game); }}>
            <FiHeart size={18} fill={fav ? 'currentColor' : 'none'} />
          </IconBtn>
          <IconBtn><FiShare2 size={18} /></IconBtn>
        </TopBar>
      </Hero>

      <Body>
        <GameTitle>{game.title}</GameTitle>

        <Meta>
          {platforms.map(p => <PlatChip key={p}>{p?.trim()}</PlatChip>)}
          {game.genre && <GenreChip>{game.genre}</GenreChip>}
          {game.type_game && game.type_game !== 'Игра' && <MetaChip>{game.type_game}</MetaChip>}
        </Meta>

        <PriceSection>
          <PriceSectionTitle>Выберите регион</PriceSectionTitle>
          {REGIONS.map(r => {
            const rp = game.prices?.[r.key];
            if (!rp?.price_rub) return null;
            const disc = rp.old_price_rub ? Math.round(((rp.old_price_rub - rp.price_rub) / rp.old_price_rub) * 100) : 0;
            const isBest = r.key === minRegion;
            const isSelected = selectedRegion === r.key;
            return (
              <RegionRow 
                key={r.key} 
                $selected={isSelected}
                onClick={() => { hapticFeedback?.('light'); setSelectedRegion(r.key); }}
              >
                <RegionInfo>
                  <RegionFlag src={r.flag} alt={r.name} />
                  <div>
                    <RegionName>
                      {r.name}
                      {isBest && <BestBadge src="/icons/best-price.png?v=1" alt="Лучшая цена" />}
                    </RegionName>
                    {(game.rus_voice || game.rus_screen) && (
                      <LangBadges>
                        {game.rus_voice && <LangBadge>🎙 Озвучка</LangBadge>}
                        {game.rus_screen && <LangBadge>💬 Субтитры</LangBadge>}
                      </LangBadges>
                    )}
                  </div>
                </RegionInfo>
                <RegionPriceWrap>
                  <div style={{ textAlign: 'right' }}>
                    <RegionPrice $best={isBest}>
                      {rp.price_rub.toLocaleString('ru')} ₽
                    </RegionPrice>
                    {rp.old_price_rub && <RegionOld>{rp.old_price_rub.toLocaleString('ru')} ₽</RegionOld>}
                  </div>
                  {disc > 0 && <DiscBadge>−{disc}%</DiscBadge>}
                </RegionPriceWrap>
              </RegionRow>
            );
          })}
          {game.discount_end_date && (
            <DiscountNote>
              Скидка до {new Date(game.discount_end_date).toLocaleDateString('ru')}
            </DiscountNote>
          )}
        </PriceSection>

        <SpecsSectionTitle>Характеристики</SpecsSectionTitle>
        <SpecsCard>
          <SpecRow><SpecLabel>Платформа</SpecLabel><SpecVal>{game.platform}</SpecVal></SpecRow>
          {game.genre && <SpecRow><SpecLabel>Жанр</SpecLabel><SpecVal>{game.genre}</SpecVal></SpecRow>}
          {game.type_game && <SpecRow><SpecLabel>Тип</SpecLabel><SpecVal>{game.type_game}</SpecVal></SpecRow>}
          {game.publisher && <SpecRow><SpecLabel>Издатель</SpecLabel><SpecVal>{game.publisher}</SpecVal></SpecRow>}
          <SpecRow>
            <SpecLabel>Рус. озвучка</SpecLabel>
            <SpecVal><CheckIcon $yes={game.rus_voice}>{game.rus_voice ? '✓' : '✕'}</CheckIcon>{game.rus_voice ? 'Да' : 'Нет'}</SpecVal>
          </SpecRow>
          <SpecRow>
            <SpecLabel>Рус. субтитры</SpecLabel>
            <SpecVal><CheckIcon $yes={game.rus_screen}>{game.rus_screen ? '✓' : '✕'}</CheckIcon>{game.rus_screen ? 'Да' : 'Нет'}</SpecVal>
          </SpecRow>
        </SpecsCard>
      </Body>

      <BottomBar>
        <BottomLeft>
          <BottomPrice>
            {selectedRegion ? (
              <>{game.prices?.[selectedRegion]?.price_rub?.toLocaleString('ru')} ₽</>
            ) : (
              <>от {minPrice.toLocaleString('ru')} ₽</>
            )}
          </BottomPrice>
          {saving > 0 && <BottomSaving>Экономия: {saving.toLocaleString('ru')} ₽</BottomSaving>}
          {!selectedRegion && <BottomHint>выберите регион ↑</BottomHint>}
        </BottomLeft>
        <OrderBtn
          $inCart={inCart}
          $disabled={!selectedRegion && !inCart}
          onClick={() => {
            if (!selectedRegion && !inCart) {
              hapticFeedback?.('error');
              return;
            }
            hapticFeedback?.('medium');
            if (inCart) {
              navigate('/cart');
              return;
            }
            const regionData = game.prices?.[selectedRegion];
            addItem({
              ...game,
              selectedRegion,
              selectedPrice: regionData?.price_rub,
              selectedRegionName: REGIONS.find(r => r.key === selectedRegion)?.name,
              selectedRegionFlag: REGIONS.find(r => r.key === selectedRegion)?.flag,
            });
          }}
        >
          {inCart ? <FiCheck size={18} /> : <FiShoppingCart size={18} />}
          {inCart ? 'К оформлению' : (selectedRegion ? 'В корзину' : 'Выберите регион')}
        </OrderBtn>
      </BottomBar>
    </Page>
  );
};

export default GameDetails;
